import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo } from 'react';

/**
 * Maximum allowed length for query parameters to prevent DoS attacks
 */
const MAX_PARAM_LENGTH = 500;

/**
 * Validates that a returnTo path is safe for internal redirects
 *
 * **Security Checks:**
 * - Must start with / (internal path only)
 * - No protocol schemes (http://, https://, javascript:, etc.)
 * - No backslash escaping (prevents \/\/evil.com bypasses)
 * - Length limit enforcement
 * - No suspicious characters
 *
 * @param value - The returnTo path to validate
 * @returns True if the path is safe for internal redirect
 *
 * @security OWASP A01:2021 - Broken Access Control (Open Redirect Prevention)
 */
const isValidReturnTo = (value: string | null): boolean => {
  if (!value || typeof value !== 'string') return false;

  const trimmedValue = value.trim();

  // Length check: prevent DoS via extremely long params
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;

  // SECURITY: Must be a relative path starting with /
  // This prevents open redirects to external sites
  if (!trimmedValue.startsWith('/')) return false;

  // SECURITY: Block protocol schemes (even if after /)
  // Prevents: /..//evil.com, /\evil.com, //evil.com
  if (/^\/\/|\\|:\/\/|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;

  // SECURITY: Block suspicious patterns
  if (/<script|%3Cscript/i.test(trimmedValue)) return false;

  return true;
};

/**
 * Validates a generic query parameter for safety
 *
 * @param value - The query parameter value to validate
 * @returns True if the value is safe
 */
const isValidParam = (value: string | null): boolean => {
  if (!value || typeof value !== 'string') return false;
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;
  // XSS prevention
  if (/<script|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;
  return true;
};

export interface UseLoginResult {
  /**
   * Function to initiate Auth0 login with redirect
   */
  handleLogin: () => void;
  /**
   * Whether the user was just logged out
   */
  isLoggedOut: boolean;
  /**
   * Whether the session expired (401 error)
   */
  isSessionExpired: boolean;
  /**
   * Whether an authentication error occurred (e.g., invalid user)
   */
  isAuthError: boolean;
  /**
   * Whether the user identity could not be loaded (e.g., backend unavailable)
   */
  isIdentityError: boolean;
  /**
   * Whether the user is currently authenticated
   */
  isAuthenticated: boolean;
  /**
   * Whether authentication state is loading
   */
  isLoading: boolean;
}

/**
 * Hook for handling login page logic
 *
 * **Security Features:**
 * - Input validation on all query parameters
 * - Open redirect prevention (returnTo must be internal path)
 * - XSS prevention through parameter sanitization
 * - DoS prevention via length limits
 * - No sensitive data logging
 *
 * @example
 * ```tsx
 * const MyLoginPage = () => {
 *   const { handleLogin, isLoggedOut } = useLogin();
 *
 *   return (
 *     <div>
 *       <h1>Welcome</h1>
 *       {isLoggedOut && <p>You have been logged out</p>}
 *       <button onClick={handleLogin}>Login</button>
 *     </div>
 *   );
 * };
 * ```
 *
 * @security Implements OWASP Top 10 secure coding practices
 */
export function useLogin(): UseLoginResult {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  // Check if user was logged out or session expired (from query params)
  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  // SECURITY: Validate query parameters before use
  const loggedOutParam = searchParams.get('loggedOut');
  const sessionExpiredParam = searchParams.get('sessionExpired');
  const errorParam = searchParams.get('error');

  const isLoggedOut = isValidParam(loggedOutParam) && loggedOutParam === 'true';
  const isSessionExpired = isValidParam(sessionExpiredParam) && sessionExpiredParam === 'true';
  const isAuthError = isValidParam(errorParam) && errorParam === 'auth';
  const isIdentityError = isValidParam(errorParam) && errorParam === 'identity';

  // Clean up the URL after reading the query param (preserve returnTo)
  useEffect(() => {
    const hasLoggedOut = isLoggedOut;
    const hasSessionExpired = isSessionExpired;
    const hasError = isAuthError || isIdentityError;
    const returnToParam = searchParams.get('returnTo');

    if ((hasLoggedOut || hasSessionExpired || hasError) && typeof window !== 'undefined') {
      // SECURITY: Validate returnTo before preserving it
      const validReturnTo = returnToParam && isValidReturnTo(returnToParam) ? returnToParam : null;
      const newUrl = validReturnTo
        ? `/login?returnTo=${encodeURIComponent(validReturnTo)}`
        : '/login';

      window.history.replaceState(
        {
          loggedOut: hasLoggedOut,
          sessionExpired: hasSessionExpired,
          authError: hasError,
        },
        '',
        newUrl
      );
    }
  }, [isLoggedOut, isSessionExpired, isAuthError, isIdentityError, searchParams]);

  const handleLogin = () => {
    // SECURITY: Validate returnTo parameter before using it
    const urlParams = new URLSearchParams(window.location.search);
    const returnToParam = urlParams.get('returnTo');

    // SECURITY: Only use returnTo if it's a valid internal path
    // This prevents open redirect vulnerabilities
    const returnTo = returnToParam && isValidReturnTo(returnToParam) ? returnToParam : '/';

    // SECURITY: No logging of potentially malicious parameter values
    loginWithRedirect({
      appState: {
        returnTo, // Safe: validated internal path only
      },
    });
  };

  return {
    handleLogin,
    isLoggedOut,
    isSessionExpired,
    isAuthError,
    isIdentityError,
    isAuthenticated,
    isLoading,
  };
}
